import { IAPICodeDTO, ItemValue } from '$/types'
import { selectDataLabel } from 'common/utils/chart/chartUtils'

import {
  IChartData,
  IChartDataValues,
  IChartSeriesLabels,
  IQRAChartBaseObject,
  IQRAChartObjects,
  IQRALinearReferenceChartObject,
  IQRAStandardChartObject,
  IQRAThreatChartObject,
  IThreatCategoryTooltipGroup,
  IThreatCategoryTooltipSeriesValues,
  QRAGraphType
} from '.'
import { IThreshold } from './Implementations/GraphLinearReference'

export const wasRiskLevelFiltered = (
  idx: number,
  riskLevels: Array<IAPICodeDTO>,
  categories: Array<string>
) => {
  if (riskLevels.length === 0) {
    return false
  }

  const levelsFiltered = riskLevels.map((level) => level.name)

  return !levelsFiltered.includes(categories[idx])
}

export const filterS2Values = (
  s1Values: number[],
  s2Values: number[],
  riskLevels: Parameters<typeof wasRiskLevelFiltered>[1],
  categories: Parameters<typeof wasRiskLevelFiltered>[2]
): number[] => {
  const s2NonFilteredValues: number[] = s2Values

  s1Values.forEach((value, idx) => {
    /*
        if some s1Value have 0 risk, but then we run WIS (s2Value) turning up pipe pressure and risk appears
        we shouldn't hide risks in those scenarios
      */
    if (
      value ||
      (!value && !wasRiskLevelFiltered(idx, riskLevels, categories))
    ) {
      /*
          use a sparsed array not to loose order when is important,
          as an example to match against colors like Risk Level
        */
      s2NonFilteredValues[idx] = s2Values[idx]
    } else {
      s2NonFilteredValues[idx] = 0
    }
  })

  return s2NonFilteredValues
}

export const getCategoryDataForState = (
  chart: IQRAChartBaseObject,
  stateName: ItemValue
): number[] => {
  let stateCategoryValues: number[] = []

  switch (chart.graphType) {
    case QRAGraphType.LINEAR_REFERENCE: {
      let prevSegmentToMeasure: number | undefined

      stateCategoryValues = (
        chart as IQRALinearReferenceChartObject
      ).items.reduce((accumulator: number[], category) => {
        let res: number | undefined

        // Checks if theres gaps in the pipe segments and inserts dummy value to be replaced by null later
        // Shouldn't replace with null now as it affects the other charts that we have
        if (
          prevSegmentToMeasure &&
          prevSegmentToMeasure !== category.values[0].value.fromMeasure
        ) {
          accumulator.push(-1)
        }

        category.values.forEach((v) => {
          if (v.key === stateName.key) {
            if (res != null) {
              console.warn('Duplicate state found in chart object', category.id)
            }

            res = v.value.risk
          }
        })
        // If state does not exist for a category, push placeholder `0` to it..
        accumulator.push(res ?? 0)
        prevSegmentToMeasure = category.values[0].value.toMeasure

        return accumulator
      }, stateCategoryValues)

      break
    }

    default: {
      ;(chart as IQRAStandardChartObject).items.forEach((category) => {
        let res: number | undefined

        category.values.forEach((v: { key: string; value: number }) => {
          if (v.key === stateName.key) {
            if (res != null) {
              console.warn(
                'Duplicate state found in chart object',
                category.name
              )
            }

            res = v.value
          }
        })
        // If state does not exist for a category, push placeholder `0` to it..
        stateCategoryValues.push(res ?? 0)
      })
    }
  }

  return stateCategoryValues
}

export const getFormattedChartValues = (
  chart: IQRAChartBaseObject,
  stateNames: ItemValue[]
) => {
  const seriesValues: IChartDataValues = {}
  const seriesLabels: IChartSeriesLabels = {}

  for (const stateName of stateNames) {
    const _key = stateName.key

    seriesValues[_key] = getCategoryDataForState(chart, stateName)
    seriesLabels[_key] = selectDataLabel(stateName)
  }

  return { seriesValues, seriesLabels }
}

const createEmptyChartData = (): IChartData => ({
  categoryIds: [],
  categories: [],
  values: {},
  labels: {}
})

const getThreatCategorySeriesValues = (
  stateNames: ItemValue[],
  values: Array<{ key: string; value: number }>
): IThreatCategoryTooltipSeriesValues => {
  const seriesValues: IThreatCategoryTooltipSeriesValues = {}

  for (const stateName of stateNames) {
    seriesValues[stateName.key] =
      values.find((value) => value.key === stateName.key)?.value ?? 0
  }

  return seriesValues
}

const getStandardChartData = (chart?: IQRAStandardChartObject): IChartData => {
  if (chart == null || chart.items.length < 1) {
    return createEmptyChartData()
  }

  const categoryIds = chart.items.map((i) => i.id)
  const categories = chart.items.map((i) => i.name)

  const stateNames = chart.items.reduce((arr: ItemValue[], cur) => {
    for (const categoryValue of cur.values) {
      if (!arr.find((cat) => cat.key === categoryValue.key)) {
        arr.push(categoryValue)
      }
    }

    return arr
  }, [])

  const { seriesValues, seriesLabels } = getFormattedChartValues(
    chart,
    stateNames
  )

  return {
    categoryIds,
    categories,
    values: seriesValues,
    labels: seriesLabels
  }
}

/**
 * NOTE: Only use this method for simple charts (i.e. basic line / bar / pie chart)
 */
export const getStandardChartCVPairs = (
  charts: IQRAChartObjects,
  chartType: QRAGraphType
): IChartData => {
  const chart = charts[chartType] as IQRAStandardChartObject | undefined

  /**
   * NOTE: Graph component structure example
   * categoryIds = [1, 2, ...]
   * categories = ['External Corrosion', 'Internal Corrosion', ...]
   * values = {
   *   state1 = ['External Corrosion value', 'Internal Corrosion Value', ...]
   *   state2 = ['External Corrosion value', 'Internal Corrosion Value', ...]
   * }
   */
  return getStandardChartData(chart)
}

export const getThreatCategoryChartCVPairs = (
  charts: IQRAChartObjects,
  chartType: QRAGraphType
): IChartData => {
  const chart = charts[chartType] as IQRAThreatChartObject | undefined
  const groupingConfig = chart?.groupingConfig?.[0]
  const sourceCategories = chart?.sourceCategories

  if (
    chart == null ||
    sourceCategories == null ||
    sourceCategories.length < 1 ||
    groupingConfig == null ||
    groupingConfig.groupings.length < 1
  ) {
    return createEmptyChartData()
  }

  const sourceCategoriesByKey = new Map(
    sourceCategories.map((category) => [category.key, category])
  )
  const stateNames = sourceCategories.reduce((arr: ItemValue[], cur) => {
    for (const categoryValue of cur.values) {
      if (!arr.some((stateName) => stateName.key === categoryValue.key)) {
        arr.push(categoryValue)
      }
    }

    return arr
  }, [])

  const threatCategoryTooltipGroups: IThreatCategoryTooltipGroup[] =
    groupingConfig.groupings.map((grouping, index) => {
      const members = grouping.members.reduce<
        IThreatCategoryTooltipGroup['members']
      >((groupedMembers, memberKey) => {
        const category = sourceCategoriesByKey.get(memberKey)

        if (category == null) {
          return groupedMembers
        }

        groupedMembers.push({
          key: category.key,
          label: category.label,
          sortOrder: category.sortOrder,
          values: getThreatCategorySeriesValues(stateNames, category.values)
        })

        return groupedMembers
      }, [])

      const totals = members.reduce<IThreatCategoryTooltipSeriesValues>(
        (seriesTotals, member) => {
          for (const stateName of stateNames) {
            seriesTotals[stateName.key] += member.values[stateName.key] ?? 0
          }

          return seriesTotals
        },
        stateNames.reduce<IThreatCategoryTooltipSeriesValues>(
          (seriesTotals, stateName) => {
            seriesTotals[stateName.key] = 0

            return seriesTotals
          },
          {}
        )
      )

      return {
        key: grouping.key,
        label: grouping.label,
        sortOrder: grouping.sortOrder ?? index + 1,
        members,
        totals
      }
    })

  const aggregatedChart: IQRAStandardChartObject = {
    graphType: chart.graphType,
    items: threatCategoryTooltipGroups.map((grouping, index) => ({
      id: grouping.sortOrder ?? index + 1,
      name: grouping.label,
      values: stateNames.map((stateName) => ({
        key: stateName.key,
        value: grouping.totals[stateName.key] ?? 0
      }))
    }))
  }

  return {
    ...getStandardChartData(aggregatedChart),
    threatCategoryTooltipGroups
  }
}

export const getLRGraphData = (
  charts: IQRAChartObjects,
  chartType: QRAGraphType
) => {
  const XValues: number[] = []
  const measureValues: { fromMeasure: number; toMeasure: number }[] = []
  let threshold: IThreshold[] = []
  const chart = charts[chartType] as IQRALinearReferenceChartObject | undefined

  if (chart == null || chart.items.length < 1) {
    return {
      XValues: [],
      threshold: [
        { id: 0, code: '', name: '', minValue: 0, maxValue: 0, colour: '' }
      ],
      measureValues: [],
      seriesValues: {},
      seriesLabels: {}
    }
  }

  const stateNames = chart.items.reduce(
    (
      arr: ItemValue[],
      cur: IQRALinearReferenceChartObject['items'][number]
    ) => {
      for (const categoryValue of cur.values) {
        if (!arr.find((cat) => cat.key === categoryValue.key)) {
          arr.push({ key: categoryValue.key, value: 0 })
        }
      }

      return arr
    },
    []
  )

  const { seriesValues, seriesLabels } = getFormattedChartValues(
    chart,
    stateNames
  )

  threshold = chart.acceptanceCategories

  for (let i = 0; i < chart?.items?.length; i++) {
    const fromMeasure = chart?.items[i].values[0].value.fromMeasure
    const toMeasure = chart?.items[i].values[0].value.toMeasure

    measureValues.push({ fromMeasure, toMeasure })

    if (i == 0) {
      XValues.push(fromMeasure)
    }

    if (i < chart?.items?.length - 1) {
      const nextFromMeasure = chart?.items[i + 1].values[0].value.fromMeasure
      const currentToMeasure = chart?.items[i].values[0].value.toMeasure

      if (currentToMeasure === nextFromMeasure) {
        XValues.push(currentToMeasure)
      } else {
        XValues.push(currentToMeasure)
        XValues.push(nextFromMeasure)

        measureValues.push({
          fromMeasure: currentToMeasure,
          toMeasure: nextFromMeasure
        })

        if (stateNames.length === 2) {
          const nextToMeasure = chart.items[i + 1].values[0].value.toMeasure

          measureValues.push({
            fromMeasure: nextFromMeasure,
            toMeasure: nextToMeasure
          })
        }
      }
    } else {
      XValues.push(toMeasure)
    }
  }

  return { XValues, seriesValues, threshold, seriesLabels, measureValues }
}
